import { ArrowRight, UserCircle } from "@phosphor-icons/react";
import { type FormEvent, useState } from "react";

import { getApiErrorMessage, useConfirmWifiLink } from "@/lib/api";

interface ConfirmStepProps {
  token: string;
  hintEmail: string;
  hintName: string | null | undefined;
  onConfirmed: (identityAnswer: string) => void;
}

/**
 * Wrong-recipient check before the form.
 *
 * The masked hint is enough for the right person to recognise themselves and
 * not enough for a stranger holding a forwarded link to learn the household's
 * email or surname. The server owns the comparison; this component only
 * relays the answer and the error.
 */
export function ConfirmStep({ token, hintEmail, hintName, onConfirmed }: ConfirmStepProps) {
  const [answer, setAnswer] = useState("");
  const [error, setError] = useState("");
  const confirm = useConfirmWifiLink();

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!answer.trim()) {
      setError("Enter the email address or surname we have for you.");
      return;
    }
    setError("");
    try {
      await confirm.mutateAsync({ token, identityAnswer: answer });
      onConfirmed(answer);
    } catch (err) {
      setError(getApiErrorMessage(err, "That does not match our records."));
    }
  };

  return (
    <div className="max-w-[570px]">
      <p className="text-accent mb-4 text-[12px] tracking-[0.1em] uppercase">
        Before we pack your kit
      </p>
      <h1 className="text-primary text-[42px] leading-[1.06] tracking-[-0.035em] sm:text-[56px] lg:text-[62px]">
        First, let us check this is you.
      </h1>
      <p className="text-muted mt-6 max-w-[520px] text-[18px] leading-7">
        We are preparing a Smart Climate kit for {hintEmail}
        {hintName ? `, in the name of ${hintName}` : ""}. Confirm your details so we know the right
        home is getting the right kit.
      </p>

      <form onSubmit={handleSubmit} noValidate className="mt-9">
        <div className="space-y-5 rounded-2xl bg-white p-5 shadow-[0_16px_50px_-34px_rgba(40,35,31,0.45)] sm:p-7">
          <div>
            <label htmlFor="identity-answer" className="text-primary block text-[14px] leading-5">
              Your email address or surname
            </label>
            <p className="text-muted mt-1 text-[12px] leading-4">
              Either one works. Use the address this link was sent to.
            </p>
            <div className="relative mt-2">
              <UserCircle
                className="text-muted absolute top-1/2 left-4 size-5 -translate-y-1/2"
                weight="light"
                aria-hidden
              />
              <input
                id="identity-answer"
                name="identity-answer"
                value={answer}
                onChange={(event) => {
                  setAnswer(event.target.value);
                  setError("");
                }}
                autoComplete="off"
                aria-invalid={Boolean(error)}
                aria-describedby={error ? "identity-answer-error" : undefined}
                placeholder="e.g. your@email.com"
                className="border-border text-primary placeholder:text-muted focus:border-ring focus:ring-accent/15 h-13 w-full rounded-xl border bg-white pr-4 pl-12 text-[16px] transition outline-none focus:ring-4"
              />
            </div>
            {error && (
              <p id="identity-answer-error" className="text-destructive mt-2 text-[12px] leading-4">
                {error}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={confirm.isPending}
            className="bg-accent text-accent-foreground hover:bg-accent-pressed focus-visible:outline-ring flex h-13 w-full items-center justify-center gap-2 rounded-md px-6 text-[15px] transition focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-wait disabled:opacity-70"
          >
            {confirm.isPending ? "Checking…" : "Continue"}
            {!confirm.isPending && <ArrowRight className="size-4" weight="bold" />}
          </button>
        </div>
      </form>
    </div>
  );
}
